import { ChevronLeft, ChevronRight, FileText, LayoutDashboard, type LucideIcon, Users } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserProfile } from '@/components/UserProfile';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { OrganizationSwitcher } from '../organization-switcher/OrganizationSwitcher';

const SIDEBAR_COLLAPSIBLE_CLASSES =
  'px-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0';

type NavItem = {
  id: string;
  title: string;
  href: string;
  icon: LucideIcon;
  children?: ReadonlyArray<{ id: string; title: string; href: string }>;
};

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'dashboard', title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    id: 'extractors',
    title: 'Document Tools',
    href: '/extractors',
    icon: FileText,
    children: [
      { id: 'payslip', title: 'Payslip Extractor', href: '/extractors/payslip' },
      { id: 'capital-gains', title: 'Capital Gains Extractor', href: '/extractors/capital-gains' },
      { id: 'document-splitter', title: 'US Return Splitter', href: '/extractors/document-splitter' },
    ],
  },
  { id: 'staff', title: 'Staff', href: '/staff', icon: Users },
];

type AppSidebarProps = ComponentProps<typeof Sidebar>;

export const AppSidebar = ({ className, ...props }: AppSidebarProps) => {
  const { pathname } = useLocation();

  return (
    <Sidebar collapsible="icon" {...props} className={cn('bg-sidebar', className)}>
      <SidebarTrigger className="-right-3 absolute top-12 z-10 size-6 rounded-full border border-border bg-background shadow-md hover:bg-sidebar-accent">
        <ChevronRight className="size-4 group-data-[state=expanded]:hidden" />
        <ChevronLeft className="size-4 group-data-[state=collapsed]:hidden" />
      </SidebarTrigger>

      <SidebarContent className="overflow-x-hidden">
        {/* Logo */}
        <div className="flex shrink-0 items-center px-4 pt-6 pb-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <h1 className="font-heading font-semibold text-2xl text-foreground">
            <span className="group-data-[collapsible=icon]:hidden">Tax Pod</span>
            <span className="hidden group-data-[collapsible=icon]:inline">T</span>
          </h1>
        </div>

        {/* Organisation Switcher */}
        <SidebarMenu className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'pt-2')}>
          <OrganizationSwitcher />
        </SidebarMenu>

        <SidebarSeparator />

        {/* Navigation — flex-1 pushes content to top within SidebarContent */}
        <div className="flex flex-1 flex-col">
          <SidebarMenu className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'pt-2')}>
            {NAV_ITEMS.map((item) => (
              <SidebarMenuItem className="flex flex-col" key={item.id}>
                <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.title}>
                  <Link to={item.href}>
                    <item.icon className="size-6" />
                    <span className="font-body font-medium text-sm group-data-[collapsible=icon]:hidden">
                      {item.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
                {item.children && (pathname === item.href || pathname.startsWith(`${item.href}/`)) ? (
                  <SidebarMenuSub>
                    {item.children.map((child) => (
                      <SidebarMenuSubItem key={child.id}>
                        <SidebarMenuSubButton asChild isActive={pathname === child.href}>
                          <Link to={child.href}>{child.title}</Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                ) : null}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </div>
      </SidebarContent>

      <SidebarFooter className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'py-4')}>
        <UserProfile />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
